import React, { type ReactNode } from 'react';
import { styled } from '@compiled/react';
import Icon from '@atlaskit/icon';
import ArrowRightIcon from '@atlaskit/icon/glyph/arrow-right';
import IssuesIcon from '@atlaskit/icon/glyph/issues';
import { JiraIcon } from '@atlaskit/logo';
import { LinkItem } from '@atlaskit/menu';
import { colors } from '@atlaskit/theme';
import { token } from '@atlaskit/tokens';
// eslint-disable-next-line jira/restricted/@atlassian+jira-common-styles
import { gridSize } from '@atlassian/jira-common-styles/src';
import { useIntl } from '@atlassian/jira-intl';
import { fireUIAnalytics, useAnalyticsEvents } from '@atlassian/jira-product-analytics-bridge';
import { SlackGlyph, LaunchDarklyGlyph } from '../../../common/assets';

import type { ProjectProps } from '../../../common/types';
import {
	buildDiscoverAutomationUrl,
	buildTemplateRuleAutomationUrl,
} from '../../../common/utils/automation-urls';
import messages from './messages';

const jiraIssuesIcon = <IssuesIcon size="medium" label="" />;
const slackIcon = <Icon size="medium" glyph={SlackGlyph} label="" />;
const launchDarklyGliph = <Icon size="medium" glyph={LaunchDarklyGlyph} label="" />;

const ActionAndTriggerIcons = ({ trigger }: { trigger: ReactNode }) => (
	<>
		<IconShadow>
			<JiraIcon size="small" appearance="brand" />
		</IconShadow>
		<ArrowRightIcon label="" />
		<IconShadow>{trigger}</IconShadow>
	</>
);

export const AutomationMenuTemplateRules = (props: ProjectProps) => {
	const { isSimplifiedProject, projectKey, projectType } = props;
	const { formatMessage } = useIntl();
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const analyticsEvent = createAnalyticsEvent({
		action: 'clicked',
		actionSubject: 'link',
	});
	return (
		<>
			<LinkItem
				shouldTitleWrap
				href={buildTemplateRuleAutomationUrl(
					{
						projectKey,
						projectType,
						isSimplified: isSimplifiedProject,
					},
					'software_template_14',
				)}
				target="_blank"
				iconBefore={<ActionAndTriggerIcons trigger={jiraIssuesIcon} />}
				onClick={() => fireUIAnalytics(analyticsEvent, 'templateRuleAddVersionToInProgressIssue')}
			>
				{formatMessage(messages.templateRuleAddIssueToVersionOnVersionCreate)}
			</LinkItem>
			<LinkItem
				shouldTitleWrap
				href={buildTemplateRuleAutomationUrl(
					{
						projectKey,
						projectType,
						isSimplified: isSimplifiedProject,
					},
					'devops_template_6',
				)}
				target="_blank"
				iconBefore={<ActionAndTriggerIcons trigger={launchDarklyGliph} />}
				onClick={() => fireUIAnalytics(analyticsEvent, 'templateRuleCreateLaunchDarklyFlag')}
			>
				{formatMessage(messages.templateRuleCreateLaunchDarklyFlagOnVersionCreate)}
			</LinkItem>
			<LinkItem
				shouldTitleWrap
				href={buildTemplateRuleAutomationUrl(
					{
						projectKey,
						projectType,
						isSimplified: isSimplifiedProject,
					},
					'software_template_12',
				)}
				target="_blank"
				iconBefore={<ActionAndTriggerIcons trigger={slackIcon} />}
				onClick={() => fireUIAnalytics(analyticsEvent, 'templateSendSlackMessageOnVersionRelease')}
			>
				{formatMessage(messages.templateRuleSendSlackMessageOnVersionRelease)}
			</LinkItem>
			<LinkItem
				shouldTitleWrap
				href={buildTemplateRuleAutomationUrl(
					{
						projectKey,
						projectType,
						isSimplified: isSimplifiedProject,
					},
					'software_template_13',
				)}
				target="_blank"
				iconBefore={<ActionAndTriggerIcons trigger={slackIcon} />}
				onClick={() =>
					fireUIAnalytics(analyticsEvent, 'templateSendSlackMessageOnVersionUnRelease')
				}
			>
				{formatMessage(messages.templateRuleSendSlackMessageOnVersionUnRelease)}
			</LinkItem>
			<DiscoverAutomationLink
				href={buildDiscoverAutomationUrl({
					projectKey,
					projectType,
					isSimplified: isSimplifiedProject,
				})}
				onClick={() => fireUIAnalytics(analyticsEvent, 'discoverAutomationLink')}
			>
				{formatMessage(messages.discoverMoreAutomationMenuItemLabel)}
			</DiscoverAutomationLink>
		</>
	);
};

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const IconShadow = styled.span({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	height: `${4 * gridSize}px`,
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	width: `${4 * gridSize}px`,
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	borderRadius: `${0.5 * gridSize}px`,
	boxShadow: `${token(
		'elevation.shadow.raised',
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
		`0px 1px 1px ${colors.N50A}, 0px 0px 1px ${colors.N60A}`,
	)}`,
	display: 'flex',
	alignItems: 'center',
	justifyContent: 'center',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const DiscoverAutomationLink = styled.a({
	padding: `${token('space.200', '16px')} 0px ${token('space.200', '16px')} ${token(
		'space.300',
		'24px',
	)}`,
});
